import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  LayoutDashboard, Users, Tag, Trophy, Activity,
  HeartPulse, CreditCard, BarChart2, Settings,
  LogOut, ChevronLeft, ChevronRight, UserCog, Menu, X, ShieldAlert, Wallet,
} from "lucide-react";

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  roles: string[];  // qué roles pueden ver este ítem
}

const NAV_ITEMS: NavItem[] = [
  { label: "Dashboard",    path: "/dashboard",  icon: <LayoutDashboard size={18}/>, roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista","jugador","padre"] },
  { label: "Categorías",   path: "/categorias", icon: <Tag size={18}/>,            roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista"] },
  { label: "Torneos",      path: "/torneos",    icon: <Trophy size={18}/>,         roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista","jugador","padre"] },
  { label: "Jugadores",    path: "/jugadores",  icon: <Users size={18}/>,          roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista"] },
  { label: "Partidos",     path: "/partidos",   icon: <Activity size={18}/>,       roles: ["admin","dt","analista","jugador","padre"] },
  { label: "Wellness",     path: "/wellness",   icon: <HeartPulse size={18}/>,     roles: ["admin","dt","pf","fisio"] },
  { label: "Pagos",        path: "/pagos",        icon: <CreditCard size={18}/>,     roles: ["admin"] },
  { label: "Pago Online",  path: "/pago-online",  icon: <Wallet size={18}/>,         roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista","jugador","padre"] },
  { label: "Analytics",   path: "/analytics",   icon: <BarChart2 size={18}/>,       roles: ["admin","dt","analista"] },
  { label: "Staff",        path: "/staff",      icon: <UserCog size={18}/>,        roles: ["admin"] },
  { label: "Auditoría",   path: "/auditoria",  icon: <ShieldAlert size={18}/>,    roles: ["admin"] },
  { label: "Configuración",path: "/settings",   icon: <Settings size={18}/>,       roles: ["admin","dt","pf","fisio","nutricion","psicologo","analista","jugador","padre"] },
];

const ROL_LABELS: Record<string, string> = {
  admin: "Administrador", dt: "Director Técnico", pf: "Prep. Físico",
  fisio: "Fisioterapeuta", nutricion: "Nutricionista", psicologo: "Psicólogo",
  analista: "Analista", jugador: "Jugador", padre: "Padre/Tutor",
};

interface LayoutProps { children: React.ReactNode; }

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const userRol = (user?.rol || "").toLowerCase();

  const visibleItems = NAV_ITEMS.filter(item =>
    item.roles.some(r => r.toLowerCase() === userRol)
  );

  const initials = user
    ? ((user.nombre?.[0] || "") + (user.apellido?.[0] || user.nombre?.[1] || "")).toUpperCase()
    : "?";

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Logo + collapse */}
      <div className={`flex items-center justify-between px-4 py-5 border-b border-white/10 ${collapsed ? "flex-col gap-3" : ""}`}>
        {!collapsed && (
          <div>
            <div className="text-white font-bold text-base leading-tight">Academia Elite</div>
            <div className="text-blue-300 text-xs mt-0.5">HPCS v1.0</div>
          </div>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="hidden md:flex items-center justify-center w-7 h-7 rounded-lg bg-white/10 hover:bg-white/20 transition-colors text-white"
        >
          {collapsed ? <ChevronRight size={14}/> : <ChevronLeft size={14}/>}
        </button>
        <button onClick={() => setMobileOpen(false)} className="md:hidden text-white/70 hover:text-white">
          <X size={18}/>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 overflow-y-auto">
        {visibleItems.map(item => {
          const active = loc.pathname === item.path || loc.pathname.startsWith(item.path + "/");
          return (
            <button
              key={item.path}
              onClick={() => { nav(item.path); setMobileOpen(false); }}
              title={collapsed ? item.label : undefined}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all text-left group
                ${active
                  ? "bg-blue-500 text-white shadow-md shadow-blue-500/30"
                  : "text-white/60 hover:bg-white/10 hover:text-white"
                }
                ${collapsed ? "justify-center" : ""}
              `}
            >
              <span className={`flex-shrink-0 ${active ? "text-white" : "text-white/60 group-hover:text-white"}`}>
                {item.icon}
              </span>
              {!collapsed && <span className="text-sm font-medium truncate">{item.label}</span>}
            </button>
          );
        })}
      </nav>

      {/* User footer */}
      <div className={`border-t border-white/10 px-3 py-4 ${collapsed ? "flex flex-col items-center gap-2" : ""}`}>
        {!collapsed ? (
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-white text-sm font-medium truncate">{user?.nombre} {user?.apellido}</div>
              <div className="text-white/50 text-xs truncate">{ROL_LABELS[userRol] || userRol}</div>
            </div>
            <button onClick={logout} title="Cerrar sesión" className="text-white/40 hover:text-red-400 transition-colors p-1">
              <LogOut size={16}/>
            </button>
          </div>
        ) : (
          <>
            <div className="w-9 h-9 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold">
              {initials}
            </div>
            <button onClick={logout} title="Cerrar sesión" className="text-white/40 hover:text-red-400 transition-colors p-1">
              <LogOut size={14}/>
            </button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-col bg-[#0D1B2A] transition-all duration-300 flex-shrink-0 ${collapsed ? "w-16" : "w-60"}`}>
        {sidebarContent}
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 flex md:hidden">
          <div className="w-64 bg-[#0D1B2A] flex flex-col">
            {sidebarContent}
          </div>
          <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)}/>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile topbar */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 bg-[#0D1B2A] text-white">
          <button onClick={() => setMobileOpen(true)} className="p-1.5 rounded-lg bg-white/10">
            <Menu size={20}/>
          </button>
          <span className="font-bold text-sm">Academia Elite</span>
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold">{initials}</div>
        </div>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
